import { Fragment, useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Switch,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { useApi } from "../../hooks/useApi";
import { useRefreshCompanies } from "../../hooks/useRefreshCompanies";
import { useCompanyId } from "../../hooks/useCompanyId";
import {
  AlertLabels,
  AlertLabelColumn,
  AlertLabelColumnLabel,
  ALERT_LABEL_COLUMNS,
  ALERT_LABEL_MAX_LENGTH,
  SHOPIFY_MAX_ORDER_TAG_LENGTH,
  PLACEHOLDERS_BY_SOURCE,
  AlertSourceLabel,
  ALERT_LABEL_SOURCES,
} from "../../types/alertLabels";
import { AlertSource } from "../../types/alerts";
import { CompanySettings } from "../../types/companySettings";
import { previewOrderTags, previewRefundReason } from "../../utils/alertLabelTemplate";
import { formatCurrency } from "../../utils/formatCurrency";
import RefundMaxAmountDialog from "./RefundMaxAmountDialog";
import SettingRow from "./SettingRow";

interface Props {
  companyId: string;
  companyName?: string;
}

type ToggleKey = "disputes_enabled" | "representment_enabled" | "ethoca_visa_refund_enabled" | "order_insights_enabled" | "oi_audit_excluded" | "vamp_rates_enabled";

const errorMessage = (err: unknown, fallback: string) =>
  err instanceof Error && err.message ? err.message : fallback;

// MUI's Switch carries 12px of internal padding, so its track would stop 12px
// short of the Select's border. Cancel it so every control shares one right edge.
const SWITCH_SX = { mr: -1.5 };

const EMPTY_LABELS: Record<AlertLabelColumn, AlertLabels> = {
  alert_order_tags: {},
  alert_refund_reasons: {},
};

// One ref map keyed by both, so a caret insert lands in the field that was focused.
const inputKey = (column: AlertLabelColumn, source: AlertSource) => `${column}:${source}`;


export default function CompanySettingsManager({ companyId, companyName }: Props) {
  const { get, patch, del } = useApi();

  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [disputesEnabled, setDisputesEnabled] = useState(true);
  const [representmentEnabled, setRepresentmentEnabled] = useState(true);
  const [ethocaVisaRefundEnabled, setEthocaVisaRefundEnabled] = useState(true);
  const [orderInsightsEnabled, setOrderInsightsEnabled] = useState(false);
  const [vampRatesEnabled, setVampRatesEnabled] = useState(false);
  const [oiAuditExcluded, setOiAuditExcluded] = useState(false);
  const [alertLabels, setAlertLabels] = useState<Record<AlertLabelColumn, AlertLabels>>(EMPTY_LABELS);
  // Separate from the saved map so the preview tracks typing while the PATCH waits for blur.
  const [labelDrafts, setLabelDrafts] = useState<Record<AlertLabelColumn, AlertLabels>>(EMPTY_LABELS);
  const labelInputs = useRef<Record<string, HTMLInputElement | null>>({});
  const [refundMaxAmount, setRefundMaxAmount] = useState<number>(100000);
  const [saving, setSaving] = useState(false);
  const [refundMaxOpen, setRefundMaxOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: "success" | "error" }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteConfirmName, setDeleteConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const refreshCompanies = useRefreshCompanies();
  const { setCompanyId } = useCompanyId();

  const applySettings = (settings: CompanySettings) => {
    setDisputesEnabled(settings.disputes_enabled);
    setRepresentmentEnabled(settings.representment_enabled);
    setEthocaVisaRefundEnabled(settings.ethoca_visa_refund_enabled);
    setOrderInsightsEnabled(settings.order_insights_enabled);
    setVampRatesEnabled(settings.vamp_rates_enabled);
    setOiAuditExcluded(settings.oi_audit_excluded);
    const labels = {
      alert_order_tags: settings.alert_order_tags,
      alert_refund_reasons: settings.alert_refund_reasons,
    };
    setAlertLabels(labels);
    setLabelDrafts(labels);
    setRefundMaxAmount(settings.refund_max_amount);
  };

  const fetchSettings = useCallback(async () => {
    setFetchError(null);
    setLoading(true);
    try {
      applySettings(await get<CompanySettings>(`/api/companies/${companyId}/settings`));
    } catch (err: unknown) {
      setFetchError(errorMessage(err, "Failed to load settings"));
    } finally {
      setLoading(false);
    }
  }, [get, companyId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const toggleState: Record<ToggleKey, { value: boolean; setter: (value: boolean) => void }> = {
    disputes_enabled: { value: disputesEnabled, setter: setDisputesEnabled },
    representment_enabled: { value: representmentEnabled, setter: setRepresentmentEnabled },
    ethoca_visa_refund_enabled: { value: ethocaVisaRefundEnabled, setter: setEthocaVisaRefundEnabled },
    order_insights_enabled: { value: orderInsightsEnabled, setter: setOrderInsightsEnabled },
    vamp_rates_enabled: { value: vampRatesEnabled, setter: setVampRatesEnabled },
    oi_audit_excluded: { value: oiAuditExcluded, setter: setOiAuditExcluded },
  };

  const save = async (patchBody: Record<string, unknown>, revert: () => void) => {
    setSaving(true);
    try {
      applySettings(await patch<CompanySettings>(`/api/companies/${companyId}/settings`, patchBody));
      setSnackbar({ open: true, message: "Settings updated", severity: "success" });
    } catch (err: unknown) {
      revert();
      setSnackbar({ open: true, message: errorMessage(err, "Failed to update settings"), severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (key: ToggleKey, next: boolean) => {
    const { setter, value: previous } = toggleState[key];
    setter(next);
    await save({ [key]: next }, () => setter(previous));
  };

  // Patches only the edited column: the other one is a separate setting and must not be
  // rewritten as a side effect.
  const handleLabelSave = async (column: AlertLabelColumn, source: AlertSource, next: string) => {
    const previous = alertLabels;
    if ((previous[column][source] ?? "") === next) return;
    const updated = { ...previous, [column]: { ...previous[column], [source]: next } };
    setAlertLabels(updated);
    setLabelDrafts(updated);
    await save({ [column]: updated[column] }, () => {
      setAlertLabels(previous);
      setLabelDrafts(previous);
    });
  };

  // Inserts at the caret rather than appending, so a placeholder can be dropped mid-template.
  // Two chips clicked in a row would otherwise render glued together as one unreadable tag.
  const insertPlaceholder = (column: AlertLabelColumn, source: AlertSource, token: string) => {
    const input = labelInputs.current[inputKey(column, source)];
    const current = labelDrafts[column][source] ?? "";
    const start = input?.selectionStart ?? current.length;
    const end = input?.selectionEnd ?? current.length;
    const head = current.slice(0, start);
    const insert = head && !/[\s,]$/.test(head) ? ` ${token}` : token;
    const next = head + insert + current.slice(end);
    if (next.length > ALERT_LABEL_MAX_LENGTH) {
      setSnackbar({
        open: true,
        message: `Label cannot exceed ${ALERT_LABEL_MAX_LENGTH} characters`,
        severity: "error",
      });
      return;
    }
    setLabelDrafts({ ...labelDrafts, [column]: { ...labelDrafts[column], [source]: next } });
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(start + insert.length, start + insert.length);
    });
  };

  const handleRefundMaxSave = async (cents: number) => {
    applySettings(await patch<CompanySettings>(`/api/companies/${companyId}/settings`, { refund_max_amount: cents }));
    setSnackbar({ open: true, message: "Settings updated", severity: "success" });
  };

  const handleDeleteCompany = async () => {
    setDeleting(true);
    setDeleteError(null);
    try {
      await del(`/api/companies/${companyId}`, { body: JSON.stringify({ reason: deleteReason.trim() }) });
      setDeleteOpen(false);
      await refreshCompanies();
      setCompanyId(null);
    } catch (err: unknown) {
      setDeleteError(errorMessage(err, "Failed to delete company"));
    } finally {
      setDeleting(false);
    }
  };

  const deleteNameConfirmed =
    !!companyName && deleteConfirmName.trim().toLowerCase() === companyName.trim().toLowerCase();

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <div>
      {fetchError && (
        <Alert severity="error" sx={{ mb: 2 }}>{fetchError}</Alert>
      )}

      <Paper sx={{ p: 3, maxWidth: 720 }}>
        <SettingRow
          label="Dispute management"
          description="Turn this off for clients whose disputes we do not fight. When off, the client no longer sees dispute metrics on their dashboard or the Disputes page in their portal."
          control={
            <Switch
              sx={SWITCH_SX}
              checked={disputesEnabled}
              disabled={saving}
              onChange={(e) => handleToggle("disputes_enabled", e.target.checked)}
            />
          }
        />
        <Divider />
        <SettingRow
          label="Order Insights"
          description="When on, clients at this company can open Order Insights under Deflections in their portal."
          control={
            <Switch
              sx={SWITCH_SX}
              checked={orderInsightsEnabled}
              disabled={saving}
              onChange={(e) => handleToggle("order_insights_enabled", e.target.checked)}
            />
          }
        />
        <Divider />
        <SettingRow
          label="VAMP rates"
          description="When on, clients at this company can open the VAMP rates tab on their dashboard."
          control={
            <Switch
              sx={SWITCH_SX}
              checked={vampRatesEnabled}
              disabled={saving}
              onChange={(e) => handleToggle("vamp_rates_enabled", e.target.checked)}
            />
          }
        />
        <Divider />
        <SettingRow
          label="Skip OI config audit"
          description="Excludes this company from the daily audit report (e.g. unsupported Stripe-only setups)."
          control={
            <Switch
              sx={SWITCH_SX}
              checked={oiAuditExcluded}
              disabled={saving}
              onChange={(e) => handleToggle("oi_audit_excluded", e.target.checked)}
            />
          }
        />
        <Divider />
        <SettingRow
          label="Representment"
          description="Whether we actively fight this company's disputes. When off, their disputes are hidden from the internal Disputes list by default (they can still be shown with a filter)."
          control={
            <Switch
              sx={SWITCH_SX}
              checked={representmentEnabled}
              disabled={saving}
              onChange={(e) => handleToggle("representment_enabled", e.target.checked)}
            />
          }
        />
        <Divider />
        <SettingRow
          label="Refund Visa alerts (Ethoca)"
          description="When off, Visa alerts arriving via Ethoca are not auto-refunded; they are resolved as Other / Not Refunded."
          control={
            <Switch
              sx={SWITCH_SX}
              checked={ethocaVisaRefundEnabled}
              disabled={saving}
              onChange={(e) => handleToggle("ethoca_visa_refund_enabled", e.target.checked)}
            />
          }
        />
        <Divider />
        <SettingRow
          label="Max refund amount"
          description="Default max refund amount for alerts without a payment processor account, or when the PP account has no max refund amount set."
          control={
            <Chip
              label={formatCurrency(refundMaxAmount, "USD", true, 0)}
              variant="outlined"
              color="primary"
              clickable
              onClick={() => setRefundMaxOpen(true)}
              sx={{ fontSize: "0.875rem" }}
            />
          }
        />
        <Divider />
        <Box sx={{ pt: 2, pb: 1 }}>
          <Typography sx={{ fontWeight: 600 }}>Alert labels</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Commas split the tags; the refund reason is sent whole to Shopify refunds and
            Checkout Champ.
          </Typography>
        </Box>
        {ALERT_LABEL_SOURCES.map((source) => {
          const placeholders = PLACEHOLDERS_BY_SOURCE[source];
          return (
            <Fragment key={source}>
            <SettingRow
              // Anchored to the first field so the name reads as the head of its block, not as a
              // caption floating between two unrelated inputs.
              align="flex-start"
              label={AlertSourceLabel[source]}
              control={
                <Box sx={{ width: 360 }}>
                  {ALERT_LABEL_COLUMNS.map((column) => {
                    const draft = labelDrafts[column][source] ?? "";
                    const tags = column === "alert_order_tags" ? previewOrderTags(draft) : [];
                    return (
                      <Box key={column} sx={{ mb: column === "alert_order_tags" ? 2.5 : 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          {AlertLabelColumnLabel[column]}
                        </Typography>
                        <TextField
                          size="small"
                          fullWidth
                          sx={{ mt: 0.5 }}
                          value={draft}
                          disabled={saving}
                          placeholder={`e.g. ${AlertSourceLabel[source]} Chargemont`}
                          inputRef={(el: HTMLInputElement | null) => {
                            labelInputs.current[inputKey(column, source)] = el;
                          }}
                          slotProps={{ htmlInput: { maxLength: ALERT_LABEL_MAX_LENGTH } }}
                          onChange={(e) => setLabelDrafts({
                            ...labelDrafts,
                            [column]: { ...labelDrafts[column], [source]: e.target.value },
                          })}
                          onBlur={(e) => handleLabelSave(column, source, e.target.value.trim())}
                        />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.75, minHeight: 22 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 42 }}>Insert</Typography>
                          {placeholders.map((placeholder) => (
                            <Chip
                              key={placeholder}
                              label={placeholder}
                              size="small"
                              variant="outlined"
                              clickable
                              disabled={saving}
                              // Keep focus in the field so the insert does not trigger the blur save.
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => insertPlaceholder(column, source, placeholder)}
                              sx={{ fontFamily: "monospace", fontSize: "0.7rem", height: 22 }}
                            />
                          ))}
                        </Box>
                        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mt: 0.75 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 42 }}>
                            Result
                          </Typography>
                          {column === "alert_order_tags" ? (
                            <>
                              {tags.map((tag, i) => (
                                <Chip
                                  key={`${tag}-${i}`}
                                  label={tag}
                                  size="small"
                                  color={tag.length > SHOPIFY_MAX_ORDER_TAG_LENGTH ? "warning" : "default"}
                                  sx={{ height: 22, fontSize: "0.7rem", maxWidth: 300 }}
                                />
                              ))}
                              <Typography variant="caption" color="text.secondary">
                                {`— ${tags.length} tag${tags.length === 1 ? "" : "s"}`}
                              </Typography>
                            </>
                          ) : (
                            <Chip
                              label={previewRefundReason(draft)}
                              size="small"
                              sx={{ height: 22, fontSize: "0.7rem", maxWidth: 300 }}
                            />
                          )}
                        </Box>
                        {tags.some((tag) => tag.length > SHOPIFY_MAX_ORDER_TAG_LENGTH) && (
                          <Typography variant="caption" color="warning.main" sx={{ display: "block", mt: 0.5 }}>
                            {`Shopify truncates tags over ${SHOPIFY_MAX_ORDER_TAG_LENGTH} characters`}
                          </Typography>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              }
            />
            </Fragment>
          );
        })}
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, maxWidth: 720, mt: 3, borderColor: "error.main" }}>
        <SettingRow
          label="Delete company"
          description="Blocks all of the company's users, disables its payment processor accounts and API keys, cancels its Stripe subscription, and hides it everywhere in the portal. Only the IT team can restore it."
          control={
            <Button color="error" variant="outlined" onClick={() => { setDeleteReason(""); setDeleteConfirmName(""); setDeleteError(null); setDeleteOpen(true); }}>
              Delete
            </Button>
          }
        />
      </Paper>

      <Dialog open={deleteOpen} onClose={() => !deleting && setDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{companyName ? `Delete ${companyName}?` : "Delete company"}</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This blocks all of{" "}
            <Box component="span" sx={{ fontWeight: 600 }}>{companyName ?? "this company"}</Box>'s users,
            disables its integrations and API keys, cancels its Stripe subscription with a final invoice,
            and hides the company everywhere in the portal.
          </Typography>
          <TextField
            label="Type the company name to confirm"
            helperText={companyName ? `Type "${companyName}" to confirm` : "Company name unavailable"}
            value={deleteConfirmName}
            onChange={(e) => setDeleteConfirmName(e.target.value)}
            fullWidth
            required
            disabled={deleting || !companyName}
            size="small"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Reason"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            fullWidth
            required
            multiline
            minRows={2}
            disabled={deleting}
            size="small"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleting}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteCompany}
            disabled={deleting || !deleteReason.trim() || !deleteNameConfirmed}
          >
            {deleting ? "Deleting..." : "Delete company"}
          </Button>
        </DialogActions>
      </Dialog>

      <RefundMaxAmountDialog
        open={refundMaxOpen}
        description={<>Default limit applied when an alert has no PP account, or the PP account has no max refund amount set.</>}
        initialCents={refundMaxAmount}
        onClose={() => setRefundMaxOpen(false)}
        onSave={handleRefundMaxSave}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
